import { createContext, useCallback, useContext, useEffect, useRef, useState, useTransition } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Link, useLocation, useNavigate, type LinkProps } from 'react-router-dom';
import { useDelayedFlag } from '@/hooks/ui/useDelayedFlag';
import { useAuthStore } from '@/stores/authStore';
import { navForRole, type Role } from '@/types/navigation';

/**
 * Pending-navigation feedback for the nav shells.
 *
 * Why this exists at all: navigating to a heavy view (PositionTracker is ~5.7k
 * lines and re-derives getGames() on mount) blocks long enough to look broken.
 * You cannot paint a spinner during a synchronous render — the main thread is
 * busy — so the indicator has to hang off a *transition*, which yields before
 * the heavy render. BrowserRouter already wraps its location setState in
 * React.startTransition, so that pending window exists today; the app just has
 * no way to observe it (useNavigation() needs a data router, and this app uses
 * the component <Routes> API). We re-drive the navigation inside our own
 * useTransition purely to get isPending back.
 *
 * Consumers only ever ask "which path is spinning?" — see useNavSpinner().
 * Everything else (the transition, the delay gate, the timeout, clearing) is
 * kept in here so both shells stay dumb.
 */

/** Fast transitions must stay silent; a spinner that flashes reads as a glitch. */
export const NAV_SPINNER_DELAY_MS = 150;

/**
 * When a navigation is still pending after this long, the transition is treated
 * as stuck: the spinner escalates to a visible, recoverable timeout banner
 * (React transitions aren't cancellable, so the only sound recovery is a hard
 * reload — see `retry`). A route that eventually commits still clears normally.
 */
export const NAV_SPINNER_TIMEOUT_MS = 10_000;

interface NavTransitionValue {
  /** Path whose nav item should show a spinner right now, else null. */
  spinnerPath: string | null;
  /** Path whose navigation has stalled past the timeout, else null. */
  timedOutPath: string | null;
  /** Navigate inside a transition so the pending window is observable. */
  navigateWithTransition: (to: string) => void;
  /** Escape a stuck navigation with a full document load to the target. */
  retry: () => void;
}

const NavTransitionContext = createContext<NavTransitionValue | null>(null);

/**
 * The path to render a spinner for, already delay-gated and timeout-capped.
 * Compare against a nav item's own path. Returns null outside a provider.
 */
export function useNavSpinner(): string | null {
  return useContext(NavTransitionContext)?.spinnerPath ?? null;
}

/**
 * The stalled-navigation state for the timeout banner: which destination is
 * stuck, and the recovery action. `timedOutPath` is null unless a navigation
 * has been pending past NAV_SPINNER_TIMEOUT_MS.
 */
export function useNavTimeout(): { timedOutPath: string | null; retry: () => void } {
  const ctx = useContext(NavTransitionContext);
  return { timedOutPath: ctx?.timedOutPath ?? null, retry: ctx?.retry ?? (() => {}) };
}

export function NavTransitionProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isPending, startTransition] = useTransition();
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  // Synchronously-current mirror of pendingPath. Two clicks land as two events;
  // if the second reads pendingPath from a render that hasn't committed yet it
  // sees null and fires a duplicate navigate. A ref is always current.
  const pendingRef = useRef<string | null>(null);

  const navigateWithTransition = useCallback(
    (to: string) => {
      if (pendingRef.current === to) return; // repeat click on an in-flight target
      pendingRef.current = to;
      setPendingPath(to);
      setTimedOut(false);
      startTransition(() => {
        // Mirror what Link does internally: navigating to where you already are
        // replaces rather than pushes, so it can't stack history entries.
        navigate(to, { replace: to === pathname });
      });
    },
    [navigate, pathname],
  );

  // Recovery for a stuck navigation. A soft re-navigate can't help — React
  // transitions aren't cancellable, so it would just stack a second one behind
  // the wedged first. A full document load to the target is the one move that
  // reliably escapes, at the cost of a re-bootstrap (acceptable for a rare
  // 10s stall). pendingRef is the live target even mid-render.
  const retry = useCallback(() => {
    const target = pendingRef.current;
    if (target) window.location.assign(target);
  }, []);

  // Clear-on-success. isPending falls when the new route commits — which is
  // exactly "the destination is interactive" — so the indicator cannot outlive
  // the navigation it describes. This is what makes a stranded spinner not a
  // case anyone has to handle.
  useEffect(() => {
    if (isPending) return;
    pendingRef.current = null;
    setPendingPath(null);
    setTimedOut(false);
  }, [isPending]);

  useEffect(() => {
    if (!isPending) return;
    const timer = window.setTimeout(() => setTimedOut(true), NAV_SPINNER_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isPending]);

  const delayElapsed = useDelayedFlag(isPending, NAV_SPINNER_DELAY_MS);
  // Spinner and banner are mutually exclusive: past the timeout the spinner
  // gives way to the banner. Both are gated on isPending (via timedOut/pendingPath
  // being reset when it falls), so a commit clears whichever is showing.
  const spinnerPath = delayElapsed && !timedOut ? pendingPath : null;
  const timedOutPath = timedOut && isPending ? pendingPath : null;

  return (
    <NavTransitionContext.Provider
      value={{ spinnerPath, timedOutPath, navigateWithTransition, retry }}
    >
      {children}
    </NavTransitionContext.Provider>
  );
}

// Mirrors react-router's own shouldProcessLinkClick. Anything else — cmd/ctrl
// to open a tab, shift for a window, middle click — must fall through to the
// browser untouched. Those clicks never navigate *this* tab, so claiming them
// would also strand a spinner on a navigation that never happens.
function isPlainLeftClick(e: React.MouseEvent<HTMLAnchorElement>): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * Drop-in for <Link to="..."> that routes the click through the nav transition
 * so the target's nav item can show a spinner. Renders a real <Link>, so the
 * href, back/forward, deep links and modified clicks behave exactly as before.
 * Outside a NavTransitionProvider it degrades to a plain <Link>.
 */
export function TransitionLink({
  to,
  onClick,
  ...rest
}: Omit<LinkProps, 'to'> & { to: string }) {
  const ctx = useContext(NavTransitionContext);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (!ctx) return;
    // Respect a caller that already handled it, and leave non-plain clicks to
    // the browser. Link only runs its own navigation if we don't preventDefault.
    if (e.defaultPrevented) return;
    if (!isPlainLeftClick(e)) return;
    if (rest.target && rest.target !== '_self') return;
    e.preventDefault();
    ctx.navigateWithTransition(to);
  };

  return <Link to={to} onClick={handleClick} {...rest} />;
}

/** Human label for a route path from the nav registry, or null if unknown. */
function labelForPath(role: Role, path: string): string | null {
  // Label lookup only — grant every per-user gate so any gated path still
  // resolves to its human label while the stall strip is up.
  for (const group of navForRole(role, true, true, 'researcher', false, true)) {
    for (const page of group.pages) {
      if (page.path === path) return page.label;
    }
  }
  return null;
}

/**
 * Shown when a navigation stalls past NAV_SPINNER_TIMEOUT_MS: a thin recoverable
 * strip over the (still-interactive) outgoing page. Mount once, above the routed
 * content. Renders nothing until a timeout actually occurs. Styled for the dark
 * default theme with a `light:` override — the app's `dark:` variant is inert
 * (see tailwind.config.js).
 */
export function NavTimeoutBanner() {
  const { timedOutPath, retry } = useNavTimeout();
  const role = (useAuthStore((s) => s.user?.role) ?? 'trader') as Role;
  if (!timedOutPath) return null;

  const label = labelForPath(role, timedOutPath);

  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-2 border-b border-amber-500/25 bg-amber-500/10 px-4 py-1.5 text-[11px] text-amber-200 light:text-amber-800"
    >
      <span className="flex items-center gap-2">
        <AlertTriangle size={12} />
        <span>
          {label ? (
            <>
              Still loading <strong>{label}</strong> — it's taking longer than expected.
            </>
          ) : (
            <>This page is taking longer than expected to load.</>
          )}
        </span>
      </span>
      <button
        type="button"
        onClick={retry}
        className="shrink-0 rounded border border-amber-500/40 px-2 py-0.5 font-medium hover:bg-amber-500/15"
      >
        Retry
      </button>
    </div>
  );
}
